// Select (DESIGN §3.6.5): Radix Select. Auslöser im Feld-Look (Chevron rechts) oder als
// Dropdown-Pill (`variant="pill"`, Fläche `field`, Icon + Wert + Zusatz „current“). Liste mit
// Einträgen 40, Häkchen rechts in `accent-fg`, Gruppenköpfe, max. Höhe 320.
import type { ReactNode, Ref } from 'react';
import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { Select as SelectPrimitive } from 'radix-ui';
import { fieldIcon, fieldTrigger, type FieldSize } from './_lib/fieldStyles';
import { useFieldProps } from './_lib/fieldContext';
import { floatingSurface, menuGroupLabel, menuItem } from './_lib/menuStyles';
import './_lib/motion.css';
import { useLayerStyle } from './_lib/layer';
import { cn } from '../basis/kern';

export interface SelectOption {
  value: string;
  label: string;
  /** Unterzeile `label` `fg-secondary`. */
  description?: string;
  /** Avatar 24 oder Icon 16 links. */
  leading?: ReactNode;
  disabled?: boolean;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

export type SelectEntry = SelectOption | SelectOptionGroup;

const isGroup = (entry: SelectEntry): entry is SelectOptionGroup => 'options' in entry;

export const flattenOptions = (entries: SelectEntry[]): SelectOption[] =>
  entries.flatMap((entry) => (isGroup(entry) ? entry.options : [entry]));

export interface SelectProps {
  ref?: Ref<HTMLButtonElement>;
  options: SelectEntry[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  /** `field` (Formular) oder `pill` (Filterleiste). */
  variant?: 'field' | 'pill';
  size?: FieldSize;
  /** Icon links im Auslöser (Pill: z. B. CalendarBlank für das Quartal). */
  icon?: ReactNode;
  /** Zusatz hinter dem Wert (dunkel `chip-fg`, hell `fg-tertiary`, AA), z. B. „current“. */
  suffix?: ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  onBlur?: () => void;
  className?: string;
}

export const pillTrigger = cn(
  'inline-flex h-10 max-w-full shrink-0 items-center gap-2 rounded-full bg-field px-4 text-button text-fg',
  'transition-colors duration-(--bar-dur-fast) ease-standard hover:not-disabled:bg-field-hover',
  'disabled:cursor-not-allowed disabled:text-fg-disabled',
);

export function SelectItemRow({ option }: { option: SelectOption }) {
  return (
    <SelectPrimitive.Item
      value={option.value}
      disabled={option.disabled}
      className={cn(menuItem, 'min-h-10 pr-9')}
    >
      {option.leading ? (
        <span className="inline-flex shrink-0 text-fg-secondary [&>svg]:size-4">
          {option.leading}
        </span>
      ) : null}
      <span className="flex min-w-0 flex-col">
        <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
        {option.description ? (
          <span className="text-label text-fg-secondary">{option.description}</span>
        ) : null}
      </span>
      <SelectPrimitive.ItemIndicator className="absolute right-3 inline-flex text-accent-fg">
        <CheckIcon size={16} weight="bold" aria-hidden="true" />
      </SelectPrimitive.ItemIndicator>
    </SelectPrimitive.Item>
  );
}

export function SelectList({ options }: { options: SelectEntry[] }) {
  return (
    <>
      {options.map((entry) =>
        isGroup(entry) ? (
          <SelectPrimitive.Group key={`g-${entry.label}`}>
            <SelectPrimitive.Label className={menuGroupLabel}>{entry.label}</SelectPrimitive.Label>
            {entry.options.map((option) => (
              <SelectItemRow key={option.value} option={option} />
            ))}
          </SelectPrimitive.Group>
        ) : (
          <SelectItemRow key={entry.value} option={entry} />
        ),
      )}
    </>
  );
}

export const selectContentClass = cn(
  floatingSurface,
  'max-h-[min(320px,var(--radix-select-content-available-height))] min-w-(--radix-select-trigger-width) overflow-hidden',
);

export function Select({
  ref,
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select…',
  variant = 'field',
  size = 'md',
  icon,
  suffix,
  disabled,
  required,
  name,
  onBlur,
  className,
  ...aria
}: SelectProps) {
  const field = useFieldProps({
    id: aria.id,
    'aria-describedby': aria['aria-describedby'],
    'aria-invalid': aria['aria-invalid'],
    required,
  });
  const selected = flattenOptions(options).find((option) => option.value === value);
  const layerStyle = useLayerStyle();
  return (
    <SelectPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={onValueChange}
      disabled={disabled}
      required={field.required}
      name={name}
      onOpenChange={(open) => {
        if (!open) onBlur?.();
      }}
    >
      <SelectPrimitive.Trigger
        ref={ref}
        id={field.id}
        aria-label={aria['aria-label']}
        aria-describedby={field['aria-describedby']}
        aria-invalid={field['aria-invalid']}
        data-variant={variant}
        className={cn(variant === 'pill' ? pillTrigger : fieldTrigger(size), className)}
      >
        {icon ? (
          <span aria-hidden="true" className={cn(fieldIcon, variant === 'pill' && 'text-fg')}>
            {icon}
          </span>
        ) : null}
        {selected?.leading && variant === 'field' ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {selected.leading}
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        {suffix ? (
          // Dunkel auf `field` reicht fg-tertiary nicht für AA (2,43:1) → chip-fg (4,75:1).
          <span className="shrink-0 text-chip-fg light:text-fg-tertiary">{suffix}</span>
        ) : null}
        <SelectPrimitive.Icon className={cn(fieldIcon, variant === 'pill' && 'text-fg')}>
          <CaretDownIcon aria-hidden="true" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={8}
          collisionPadding={16}
          style={layerStyle}
          className={selectContentClass}
        >
          <SelectPrimitive.Viewport className="p-1">
            <SelectList options={options} />
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}
